import { useEffect, useState } from 'react'
import { Navigate, useNavigate } from 'react-router-dom'
import { useAuth } from './AuthContext'
import { api } from '../../shared/api/http'
import './moduleChoice.css'
import { API_URL } from '../../shared/api/http'
import { WindowsDownloadLink } from '../../shared/WindowsDownloadLink'
import { StationDownloads } from '../../shared/StationDownloads'

export type SpectraModule = 'vigilancia' | 'portaria'

type ClientModuleAccess = { clientId: string; vigilanciaAllowed: boolean; portariaAllowed: boolean }

export function ModuleChoicePage() {
  const navigate = useNavigate()
  const { user, access, logout } = useAuth()
  const [loggingOut, setLoggingOut] = useState(false)
  const [vigilanciaAllowed, setVigilanciaAllowed] = useState(access.isGeneralAdministrator || access.accountAdministrator)
  const [portariaAllowed, setPortariaAllowed] = useState(false)
  const [checking, setChecking] = useState(true)
  const [loadFailed, setLoadFailed] = useState(false)
  const [retry, setRetry] = useState(0)

  useEffect(() => {
    let active = true
    setChecking(true)
    setLoadFailed(false)
    api<ClientModuleAccess[]>('/companies/modules/available').then(clients => {
      if (!active) return
      setVigilanciaAllowed(clients.some(client => client.vigilanciaAllowed) || access.isGeneralAdministrator || access.accountAdministrator)
      setPortariaAllowed(clients.some(client => client.portariaAllowed))
    }).catch(() => {
      if (active) setLoadFailed(true)
    }).finally(() => {
      if (active) setChecking(false)
    })
    return () => { active = false }
  }, [access.isGeneralAdministrator, access.accountAdministrator, retry])

  if (!user) return <Navigate to="/login" replace />
  if (!checking && vigilanciaAllowed && !portariaAllowed) return <Navigate to="/central" replace />
  if (!checking && !vigilanciaAllowed && portariaAllowed) return <Navigate to="/portaria" replace />

  function choose(module: SpectraModule) {
    navigate(module === 'portaria' ? '/portaria' : '/central')
  }

  return <main className="module-choice">
    <div className="module-choice-inner">
      <img src="/logo.png" alt="" className="module-choice-logo" />
      <span className="module-choice-eyebrow">SPECTRA GUARD AI</span>
      <h1>Seu acesso à plataforma</h1>
      <p>{checking ? 'Conferindo suas permissões…' : 'Escolha um dos módulos liberados para sua conta.'}</p>
      <div className="module-choice-grid">
        {vigilanciaAllowed && <button type="button" onClick={() => choose('vigilancia')}>
          <span className="module-choice-icon" aria-hidden="true">◉</span>
          <strong>Vigilância</strong>
          <small>Câmeras, eventos, gravações e monitoramento.</small>
          <b>Entrar em Vigilância →</b>
        </button>}
        {portariaAllowed && <button type="button" onClick={() => choose('portaria')}>
          <span className="module-choice-icon" aria-hidden="true">⌂</span>
          <strong>Portaria</strong>
          <small>Moradores, visitantes, encomendas e reservas.</small>
          <b>Entrar em Portaria →</b>
        </button>}
      </div>
      {!checking && loadFailed && <div role="alert"><p>Não foi possível conferir suas permissões. Tente novamente.</p><button type="button" className="module-choice-logout" onClick={()=>setRetry(v=>v+1)}>Conferir acesso novamente</button></div>}
      {!checking && !loadFailed && !vigilanciaAllowed && !portariaAllowed && <p>Esta conta ainda não tem um cliente autorizado. Peça ao administrador para conferir o vínculo da sua conta.</p>}
      {!checking && !vigilanciaAllowed && !portariaAllowed && <section className="module-choice-downloads" aria-label="Aplicativos para download"><h2>Aplicativos Spectra Guard AI</h2><div className="module-choice-download-links"><a href={`${API_URL}/mobile/android/download`}>↓ Baixar APK Android</a><WindowsDownloadLink/><a href="/remote/SpectraRemote-Windows-x64-Setup.exe" download>↓ Baixar Spectra Remote</a></div><StationDownloads/></section>}
      <button type="button" className="module-choice-logout" disabled={loggingOut} onClick={async () => {
        setLoggingOut(true)
        try {
          await logout()
          navigate('/login', { replace: true })
        } finally {
          setLoggingOut(false)
        }
      }}>{loggingOut ? 'Saindo…' : 'Sair da conta'}</button>
    </div>
  </main>
}
